import React, { useState, useEffect, useRef, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useTrackEvent from "@/hooks/useTrackEvent";

interface Props {
  value: string;
  onChange: (prompt: string) => void;
  configName?: string;
  onOpenVersionHistory?: () => void;
}

const SECTIONS = [
  {
    key: "identity",
    label: "Identity",
    marker: "[Identity]",
    help: "Who is this bot? What company does it represent?",
    placeholder: "You are a helpful, knowledgeable AI assistant for {Company Name}. You specialize in {products/services}.",
    defaultText: "You are a helpful, knowledgeable AI assistant. You represent this company and help visitors get accurate answers quickly. You are friendly, professional, and always honest about what you know and don't know.",
    generateHint: "Describe your company and the bot's role (e.g. 'We sell arborist supplies and this bot helps customers find gear').",
  },
  {
    key: "scope",
    label: "Scope & Topics",
    marker: "[Scope]",
    help: "What topics can the bot discuss? What should it avoid?",
    placeholder: "You may only provide information related to:\n- {Product category 1}\n- {Product category 2}\n- General questions about {your industry}",
    defaultText: "Answer questions about this company's products, services, policies, and support topics. If a question is outside your knowledge, say so honestly and offer to connect the visitor with a human agent.",
    generateHint: "What topics should the bot cover or avoid? Mention any restricted topics.",
  },
  {
    key: "rules",
    label: "Rules & Boundaries",
    marker: "[Rules]",
    help: "Specific instructions, restrictions, or behaviors the bot must follow.",
    placeholder: "You must not:\n- Make up information\n- Discuss competitors\n- Share pricing without confirmation\n\nAlways:\n- Ask for the order number before looking up orders\n- Offer to transfer to a human if you can't help",
    defaultText: "Never make up information you don't have. Never discuss competitors or make comparisons. Always offer to escalate to a human agent when you can't fully resolve the visitor's issue.",
    generateHint: "List any hard rules: what the bot must never do, what it must always do, and any compliance requirements.",
  },
  {
    key: "style",
    label: "Response Style",
    marker: "[Style]",
    help: "How should the bot communicate? Length, format, follow-ups.",
    placeholder: "Provide concise but informative responses. Anticipate common follow-up questions. Offer links only when they add value.",
    defaultText: "Keep responses concise — 2 to 4 sentences unless more detail is clearly needed. Use plain language. Ask one clarifying question at a time. End responses with a helpful follow-up question when appropriate.",
    generateHint: "Describe the tone: formal vs casual, short vs detailed, emoji usage, any specific phrasing to use or avoid.",
  },
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

interface SectionUiState {
  generating: boolean;
  input: string;
  showGenerate: boolean;
}

function parseSections(prompt: string): Record<SectionKey, string> {
  const result: Record<string, string> = { identity: "", scope: "", rules: "", style: "" };
  if (!prompt?.trim()) return result;

  const markerPattern = /\[(Identity|Scope|Rules|Style)\]\n?/gi;
  const markers: { key: string; index: number }[] = [];
  let match;
  while ((match = markerPattern.exec(prompt)) !== null) {
    markers.push({ key: match[1].toLowerCase(), index: match.index });
  }

  if (markers.length === 0) {
    result.identity = prompt.trim();
    return result;
  }

  for (let i = 0; i < markers.length; i++) {
    const start = markers[i].index + markers[i].key.length + 3;
    const end = i + 1 < markers.length ? markers[i + 1].index : prompt.length;
    result[markers[i].key] = prompt.slice(start, end).trim();
  }

  const beforeFirst = prompt.slice(0, markers[0].index).trim();
  if (beforeFirst) {
    result.identity = beforeFirst + (result.identity ? "\n\n" + result.identity : "");
  }

  return result;
}

function assembleSections(sections: Record<SectionKey, string>): string {
  return SECTIONS
    .filter((s) => sections[s.key]?.trim())
    .map((s) => `${s.marker}\n${sections[s.key].trim()}`)
    .join("\n\n");
}

function autoResize(el: HTMLTextAreaElement | null, minHeight = 80) {
  if (!el) return;
  el.style.height = "0px";
  el.style.height = Math.max(minHeight, el.scrollHeight) + "px";
}

export default function PromptEditor({ value, onChange, configName, onOpenVersionHistory }: Props) {
  const [sections, setSections] = useState<Record<SectionKey, string>>(parseSections(value));
  const [generating, setGenerating] = useState(false);
  const [generateInput, setGenerateInput] = useState("");
  const [showGenerate, setShowGenerate] = useState(false);
  const [sectionUi, setSectionUi] = useState<Record<string, SectionUiState>>({});
  const api = useApi();
  const { track } = useTrackEvent();
  const sectionRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  // Re-parse only when value changes from outside (e.g. version restore, not keystrokes)
  useEffect(() => {
    setSections(parseSections(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSectionUiField = useCallback(
    (key: string, patch: Partial<SectionUiState>) =>
      setSectionUi((prev) => {
        const existing: SectionUiState = prev[key] ?? { generating: false, input: "", showGenerate: false };
        return { ...prev, [key]: { ...existing, ...patch } };
      }),
    []
  );

  const handleSectionChange = useCallback(
    (key: SectionKey, text: string) => {
      const updated = { ...sections, [key]: text };
      setSections(updated);
      onChange(assembleSections(updated));
    },
    [sections, onChange]
  );

  async function handleGenerate() {
    if (!generateInput.trim()) return;
    setGenerating(true);
    try {
      const response = await api.admin.post("Copilot/generate-prompt", {
        description: generateInput.trim(),
        configName: configName ?? "",
      });
      if (response.ok) {
        const result = await response.json();
        if (result.prompt) {
          const parsed = parseSections(result.prompt);
          setSections(parsed);
          onChange(result.prompt);
          track("feature_click", "AIBot/PromptSaved");
          setShowGenerate(false);
          setGenerateInput("");
        }
      }
    } catch (err) {
      console.error("Prompt generation failed:", err);
    } finally {
      setGenerating(false);
    }
  }

  async function handleSectionGenerate(sectionKey: SectionKey) {
    const ui = sectionUi[sectionKey];
    const input = ui?.input?.trim();
    if (!input) return;
    setSectionUiField(sectionKey, { generating: true });
    const section = SECTIONS.find((s) => s.key === sectionKey)!;
    try {
      const response = await api.admin.post("Copilot/generate-prompt-section", {
        section: sectionKey,
        description: input,
        configName: configName ?? "",
        existingPrompt: value,
      });
      if (response.ok) {
        const result = await response.json();
        if (result.text) {
          handleSectionChange(sectionKey, result.text);
          setSectionUiField(sectionKey, { generating: false, input: "", showGenerate: false });
          track("feature_click", `AIBot/SectionGenerated/${sectionKey}`);
          return;
        }
      }
    } catch {
      // fall through to default below
    }
    // Fallback when endpoint not available or returns no text
    handleSectionChange(sectionKey, section.defaultText);
    setSectionUiField(sectionKey, { generating: false, showGenerate: false });
  }

  return (
    <div className="py-3">
      <div className="flex items-center justify-between pb-1">
        <div className="font-semibold text-sm">Instructions</div>
        <div className="flex items-center gap-2">
          {onOpenVersionHistory && (
            <>
              <button
                type="button"
                onClick={onOpenVersionHistory}
                className="text-xs text-slate-500 hover:text-slate-700 font-medium hover:underline"
              >
                Version History
              </button>
              <span className="text-slate-300">|</span>
            </>
          )}
          <button
            type="button"
            onClick={() => setShowGenerate((v) => !v)}
            className="text-xs text-purple-600 hover:text-purple-700 font-medium hover:underline"
          >
            {showGenerate ? "Cancel" : "Generate All with AI"}
          </button>
        </div>
      </div>
      <div className="text-slate-500 text-sm pb-2">
        Define how the AI chatbot interacts with users. It sets the tone, language and the behavior of the AI.
      </div>

      {showGenerate && (
        <div className="mb-3 p-3 border border-purple-200 bg-purple-50 rounded-lg">
          <div className="text-sm font-medium text-purple-800 mb-1">Describe your bot</div>
          <div className="text-xs text-purple-600 mb-2">
            Tell us about your business and what this bot should do. We'll generate all four sections for you.
          </div>
          <textarea
            value={generateInput}
            onChange={(e) => setGenerateInput(e.target.value)}
            placeholder="Example: We sell arborist supplies and tree climbing gear. The bot should help customers find products, answer questions about tree care equipment, and offer to connect them with a specialist for custom orders."
            rows={3}
            className="w-full p-2 border border-purple-200 rounded-md text-sm resize-none overflow-hidden"
          />
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating || !generateInput.trim()}
              className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {generating ? "Generating..." : "Generate All Sections"}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {SECTIONS.map((s) => {
          const current = sections[s.key];
          const ui = sectionUi[s.key];
          const isAtDefault = current === s.defaultText;
          const isEmpty = !current.trim();
          return (
            <div key={s.key} className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <div className="font-medium text-sm text-slate-700">{s.label}</div>
                  <div className="text-xs text-slate-400 hidden sm:block">{s.help}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSectionUiField(s.key, { showGenerate: !ui?.showGenerate })}
                    className="text-xs text-purple-500 hover:text-purple-700 font-medium whitespace-nowrap"
                    title="Generate this section with AI"
                  >
                    {ui?.showGenerate ? "Cancel" : "Suggest"}
                  </button>
                  {!isEmpty && !isAtDefault && (
                    <button
                      type="button"
                      onClick={() => handleSectionChange(s.key, s.defaultText)}
                      className="text-xs text-slate-400 hover:text-slate-600 whitespace-nowrap"
                    >
                      Reset
                    </button>
                  )}
                  {isEmpty && (
                    <button
                      type="button"
                      onClick={() => handleSectionChange(s.key, s.defaultText)}
                      className="text-xs text-blue-500 hover:text-blue-700 font-medium whitespace-nowrap"
                    >
                      Use default
                    </button>
                  )}
                </div>
              </div>

              {ui?.showGenerate && (
                <div className="px-3 py-2 bg-purple-50 border-b border-purple-100">
                  <div className="text-xs text-purple-600 mb-1">{s.generateHint}</div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={ui.input}
                      onChange={(e) => setSectionUiField(s.key, { input: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); handleSectionGenerate(s.key); }
                      }}
                      placeholder="Describe what this section should say..."
                      className="flex-1 p-1.5 border border-purple-200 rounded-md text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => handleSectionGenerate(s.key)}
                      disabled={ui.generating || !ui.input?.trim()}
                      className="px-2 py-1 bg-purple-600 text-white text-xs font-medium rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors whitespace-nowrap"
                    >
                      {ui.generating ? "..." : "Generate"}
                    </button>
                  </div>
                </div>
              )}

              <textarea
                ref={(el) => {
                  sectionRefs.current[s.key] = el;
                  autoResize(el, 60);
                }}
                value={current}
                onChange={(e) => {
                  handleSectionChange(s.key, e.target.value);
                  autoResize(e.target, 60);
                }}
                placeholder={s.placeholder}
                className="w-full p-3 text-sm border-0 outline-none overflow-hidden min-h-[60px] resize-none"
                style={{ height: "auto" }}
              />
            </div>
          );
        })}
        <div className="text-xs text-slate-400 px-1">
          These sections are combined into one prompt. The bot sees all sections together.
        </div>
      </div>
    </div>
  );
}
